import { FC, useEffect, useState } from "react";
import {
  DataGridPro,
  GridCellParams,
  GridColDef,
  GridColumnHeaderParams,
  GridDensity,
  GridSortModel,
  gridVisibleSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { TableProps } from "../types";
import { Alert, Box, TextField } from "@mui/material";
import { apiClient } from "api";
import TableMenu from "./components/TableMenu";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store/types";
import ExportIcon from "assets/Icons/replenishment/ExportIcon.svg";
import Alerts from "components/Alerts";
import Highlighter from "react-highlight-words";
import _ from "lodash";
import { useQuery } from "react-query";
import { addCurrentColumns, changeDensity } from "store/table/table.slice";
import {
  catalogTrackerStyle,
  catalogTrackerTableStyle,
  forecastingEditTableDownload,
  forecastingEditTableDownloadingProgress,
} from "./style";

interface CatalogAttributesTableProps extends TableProps {
  asin?: string;
}

const getColumns = (source: string) => {
  const columns: GridColDef[] = [
    {
      field: "metrics",
      headerName: "Metrics",
      flex: 1,
      headerClassName: "super-app-theme--header",
      width: 200,
      headerAlign: "center",
      align: "left",
      renderHeader: (params: GridColumnHeaderParams) => (
        <div
          style={{
            width: params.colDef.width,
          }}
        >
          {params.colDef.headerName}
        </div>
      ),
      renderCell: (params: GridCellParams) => (
        <span
          className="font-bold"
          style={{ lineHeight: 1, whiteSpace: "break-spaces", fontWeight: 900 }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "user_data",
      headerName: "User Catalog Data",
      flex: 1,
      align: "left",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => {
        const words = () => {
          if (source === "catalog_data")
            return params.row.catalog_data?.toString();
          if (source === "listing_data")
            return params.row.listing_data?.toString();
        };

        const val = _.isArray(params.value) ? params.value[0] : params.value;

        return (
          <div
            id={params.field}
            style={{
              whiteSpace: "break-spaces",
              lineHeight: 1,
            }}
          >
            {words() && val ? (
              <Highlighter
                searchWords={words().split(" ")}
                autoEscape={true}
                textToHighlight={val.toString()}
                unhighlightStyle={{ color: "red" }}
                highlightStyle={{ color: "black", lineHeight: 1.5 }}
              />
            ) : (
              val
            )}
          </div>
        );
      },
    },
    {
      field: "catalog_data",
      headerName: "Seller Central",
      type: "number",
      width: 400,
      flex: 1,
      align: "left",
      headerAlign: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (params: GridCellParams) => {
        const val = _.isArray(params.value) ? params.value[0] : params.value;
        const words = () => {
          if (source === "listing_data")
            return params.row.listing_data?.toString();
          if (source === "user_data") return params.row.user_data?.toString();
        };

        return (
          <div
            id={params.field}
            style={{
              whiteSpace: "break-spaces",
              lineHeight: 1,
            }}
          >
            {words() && val ? (
              <Highlighter
                searchWords={words().split(" ")}
                autoEscape={true}
                textToHighlight={val.toString()}
                unhighlightStyle={{ color: "red" }}
                highlightStyle={{ color: "black", lineHeight: 1.5 }}
              />
            ) : (
              val
            )}
          </div>
        );
      },
    },

    {
      field: "listing_data",
      headerName: "AMAZON.com",
      flex: 1,
      align: "left",
      headerAlign: "center",
      headerClassName: "super-app-theme--header",
      renderCell: (params: GridCellParams) => {
        const words = () => {
          if (source === "catalog_data")
            return params.row.catalog_data?.toString();
          if (source === "user_data") return params.row.user_data?.toString();
        };

        const val = _.isArray(params.value) ? params.value[0] : params.value;

        return (
          <div
            id={params.field}
            style={{
              whiteSpace: "break-spaces",
              lineHeight: 1,
            }}
          >
            {words() && val ? (
              <Highlighter
                searchWords={words().split(" ")}
                autoEscape={true}
                textToHighlight={val.toString()}
                unhighlightStyle={{ color: "red" }}
                highlightStyle={{ color: "black", lineHeight: 1.5 }}
              />
            ) : (
              val
            )}
          </div>
        );
      },
    },
  ];
  return columns;
};

const CatalogAttributesTable: FC<CatalogAttributesTableProps> = ({
  data,
  isCheckbox,
  getColumnToSort,
  sortType,
  newPageNumber,
  newPageSize,
  asin,
  fetching,
}) => {
  const dispatch = useDispatch();

  const density = useSelector(
    (state: AppState) => state.table.density
  ) as GridDensity;
  const totalRecords = useSelector(
    (state: AppState) => state.catalogTracker.records
  );
  const currentColumns = useSelector(
    (state: AppState) => state.table.currentColumns
  );

  const source = useSelector((state: AppState) => state.catalogTracker.source);
  const filters = useSelector((state: AppState) => state.table.columnFilters);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(0);
  const apiRef = useGridApiRef();
  const [initialRows, setInitialRows] = useState<any[]>([]);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [clickDownload, setClickDownload] = useState<boolean>(false);
  const [records, setRecords] = useState<number>(0);

  useEffect(() => {
    if (totalRecords) {
      setRecords(totalRecords);
    }
  }, [totalRecords]);

  useEffect(() => {
    if (
      typeof newPageNumber !== "undefined" &&
      typeof newPageSize !== "undefined"
    ) {
      setPage(newPageNumber);
      setPageSize(newPageSize);
    }
  }, [newPageNumber, newPageSize]);

  useEffect(() => {
    if (getColumns(source).length && !currentColumns?.length) {
      dispatch(addCurrentColumns(getColumns(source)));
    }
  }, [getColumns(source)]);

  useEffect(() => {
    dispatch(changeDensity("comfortable"));
    return () => {
      dispatch(changeDensity("compact"));
    };
  }, []);

  useEffect(() => {
    if (data && data?.data && Object.keys(data?.data).length) {
      const newData = [
        {
          id: 8,
          metrics: "Price ($)",
          catalog_data: data?.data?.catalog_data?.price,
          listing_data: data?.data?.listing_data?.price,
          user_data: data?.data?.user_catalog_data?.price,
        },
        {
          id: 1,
          metrics: "Title",
          catalog_data: data?.data?.catalog_data?.title,
          listing_data: data?.data?.listing_data?.title,
          user_data: data?.data?.user_catalog_data?.title,
        },

        {
          id: 9,
          metrics: "Brand",
          catalog_data: data?.data?.catalog_data?.brand,
          listing_data: data?.data?.listing_data?.brand,
          user_data: data?.data?.user_catalog_data?.brand,
        },

        {
          id: 2,
          metrics: "Bullet point 1",
          catalog_data: data?.data?.catalog_data?.bullet_points?.[0],
          listing_data: data?.data?.listing_data?.bullet_points?.[0],
          user_data: data?.data?.user_catalog_data?.bullet_points?.[0],
        },
        {
          id: 3,
          metrics: "Bullet point 2",
          catalog_data: data?.data?.catalog_data?.bullet_points?.[1],
          listing_data: data?.data?.listing_data?.bullet_points?.[1],
          user_data: data?.data?.user_catalog_data?.bullet_points?.[1],
        },
        {
          id: 4,
          metrics: "Bullet point 3",
          catalog_data: data?.data?.catalog_data?.bullet_points?.[2],
          listing_data: data?.data?.listing_data?.bullet_points?.[2],
          user_data: data?.data?.user_catalog_data?.bullet_points?.[2],
        },
        {
          id: 14,
          metrics: "Bullet point 4",
          catalog_data: data?.data?.catalog_data?.bullet_points?.[3],
          listing_data: data?.data?.listing_data?.bullet_points?.[3],
          user_data: data?.data?.user_catalog_data?.bullet_points?.[3],
        },
        {
          id: 10,
          metrics: "Bullet point 5",
          catalog_data: data?.data?.catalog_data?.bullet_points?.[4],
          listing_data: data?.data?.listing_data?.bullet_points?.[4],
          user_data: data?.data?.user_catalog_data?.bullet_points?.[4],
        },

        {
          id: 11,
          metrics: "Description",
          catalog_data: data?.data?.catalog_data?.description,
          listing_data: data?.data?.listing_data?.description,
          user_data: data?.data?.user_catalog_data?.description,
        },
      ];
      setInitialRows(newData);
    } else {
      setInitialRows([]);
    }
  }, [data]);

  const handleExport = (options: any) =>
    apiRef.current.exportDataAsCsv(options);
  const getFilteredRows = ({ apiRef }: { apiRef: any }) =>
    gridVisibleSortedRowIdsSelector(apiRef);

  return (
    <Box sx={catalogTrackerStyle}>
      <Box sx={catalogTrackerTableStyle}>
        <TableMenu disableUpload disableExpand widgetName="CatalogTracker" />

        <DataGridPro
          apiRef={apiRef}
          rows={initialRows}
          getRowHeight={() => "auto"}
          sx={{
            fontFamily: "Lato",
            fontWeight: 400,
            BorderRight: "1px solid black",
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
              borderRight: "1px solid lightgrey",
            },

            "& .MuiDataGrid-columnHeader--filledGroup": {
              background: "#BBDEFB",
              fontWeight: 600,
              BorderRight: "1px solid black",
            },
            "&  .MuiDataGrid-columnHeader--filledGroup:nth-of-type(even)": {
              background: "lightgrey",
            },

            "& .MuiDataGrid-columnHeader--sortable": {
              background: "#E3F2FD",
              fontWeight: 400,
            },

            "& .MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "center",
              fontWeight: 400,
            },

            "& .MuiDataGrid-columnHeaderTitleContainer:nth-child(2)": {
              justifyContent: "center",
              fontWeight: 400,
              background:
                source === "user_data"
                  ? "linear-gradient(90deg, #044E82 0%, #2B94DC 100%) !important"
                  : "inherit",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              justifyContent: "center",
              BorderRight: "1px solid black",
              fontWeight: 600,
              color: "#424242",
            },
            "& .MuiDataGrid-footerContainer": {
              background: "rgba(220, 227, 235, 0.3)",
              justifyContent: "start",
            },

            "&  .MuiDataGrid-cell--editable": {
              background: "rgba(25, 118, 210, 0.12) !important",
            },
            "&  .MuiDataGrid-cell--editing": {
              background: "rgba(25, 118, 210, 0.12) !important",
            },
            "& .MuiTablePagination-selectLabel ": {
              color: "#424242",
              fontSize: "14px",
              fontWeight: "400",
              fontFamily: "Lato",
            },
            "& .MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-i4bv87-MuiSvgIcon-root":
              {
                color: "rgba(1, 74, 124, 1)",
              },
            "& .MuiInputBase-root MuiInputBase-colorPrimary css-16c50h-MuiInputBase-root-MuiTablePagination-select":
              {
                borderRadius: "4px",
                margin: "10px 20px",
                border: "1px solid #9E9E9E",
              },
            "& .MuiPaginationItem-root .Mui-selected": {
              background: "linear-gradient(90deg, #034D80 0%, #2C97E0 100%);",
            },
            "& .MuiDataGrid-cell MuiDataGrid-cell--textRight": {
              background: "lightgray !important",
            },
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "6px",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "12px",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "20px",
            },
          }}
          columns={getColumns(source)}
          pageSize={pageSize}
          headerHeight={52}
          loading={fetching}
          density={density}
          checkboxSelection={isCheckbox ? true : false}
          onSortModelChange={(newSortModel: GridSortModel) => {
            if (newSortModel && newSortModel.length) {
              const { field, sort } = newSortModel[0];
              if (field && sort) {
                if (getColumnToSort) {
                  if (sortType === "asc") {
                    getColumnToSort(field, "desc");
                  } else {
                    getColumnToSort(field, sort);
                  }
                }
              }
            }
          }}
        />
      </Box>

      {clickDownload ? (
        <Box sx={forecastingEditTableDownloadingProgress}>
          <Alerts
            title={"Download in progress"}
            completeTitle={"Download complete"}
            completeSubTitle={"Restock Summary table has been exported."}
            loading={downloading}
            close={() => setClickDownload(false)}
          />
        </Box>
      ) : (
        <Box
          sx={{ ...forecastingEditTableDownload, bottom: 30, right: 50 }}
          onClick={() => handleExport({ getRowsToExport: getFilteredRows })}
        >
          <img src={ExportIcon} alt="export" />
        </Box>
      )}
    </Box>
  );
};

export default CatalogAttributesTable;
